import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createMsnConversation,
  getMsnConversations,
  getMsnDisclaimers,
  getMsnMessages,
  getMsnUnreadCount,
  markMsnRead,
  sendMsnMessage,
} from "@/features/messages/api/messagesApi";

export const msnKeys = {
  root: ["msn"] as const,
  conversations: ["msn", "conversations"] as const,
  messages: (conversationId: string | null) => ["msn", "messages", conversationId] as const,
  disclaimers: (conversationId: string | null) => ["msn", "disclaimers", conversationId] as const,
  unread: ["msn", "unread"] as const,
};

export const useMsnConversations = (enabled = true) =>
  useQuery({
    queryKey: msnKeys.conversations,
    queryFn: getMsnConversations,
    enabled,
    staleTime: 30_000,
  });

export const useMsnMessages = (conversationId: string | null, enabled = true) =>
  useQuery({
    queryKey: msnKeys.messages(conversationId),
    queryFn: () => getMsnMessages(conversationId ?? ""),
    enabled: enabled && conversationId !== null,
  });

export const useMsnDisclaimers = (conversationId: string | null, enabled = true) =>
  useQuery({
    queryKey: msnKeys.disclaimers(conversationId),
    queryFn: () => getMsnDisclaimers(conversationId ?? ""),
    enabled: enabled && conversationId !== null,
    staleTime: 5 * 60_000,
  });

export const useMsnUnreadCount = (enabled = true) =>
  useQuery({
    queryKey: msnKeys.unread,
    queryFn: getMsnUnreadCount,
    enabled,
    refetchInterval: 60_000,
  });

export const useSendMsnMessage = (conversationId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (text: string) => sendMsnMessage(conversationId ?? "", text),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: msnKeys.messages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: msnKeys.conversations });
      void queryClient.invalidateQueries({ queryKey: msnKeys.unread });
    },
  });
};

export const useMarkMsnRead = (conversationId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => markMsnRead(conversationId ?? "", messageId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: msnKeys.conversations });
      void queryClient.invalidateQueries({ queryKey: msnKeys.unread });
    },
  });
};

export const useCreateConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, professionalId }: { patientId: string; professionalId: string }) =>
      createMsnConversation(patientId, professionalId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: msnKeys.conversations });
    },
  });
};
